import { Shield, Menu, X, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

interface HeaderProps {
  /** 
   * Header variant:
   * - "landing": Fixed nav with anchor links and mobile menu (for homepage)
   * - "standard": Simple nav with Sign In button
   * - "minimal": Simple nav with Back to Home link (for legal/info pages)
   */
  variant?: "landing" | "standard" | "minimal";
}

export function Header({ variant = "standard" }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Landing page header with anchor links and mobile menu
  if (variant === "landing") {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-success-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">ZKCN</span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Features
              </a>
              <a href="#credentials" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Credentials
              </a>
              <a href="#journey" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                How It Works
              </a>
              <a href="#enterprise" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Enterprise
              </a>
              <Link to="/docs" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Docs
              </Link>
            </div>

            {/* CTA button - single unified Sign In */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/signin"
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-600" />
              ) : (
                <Menu className="w-6 h-6 text-slate-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4">
            <div className="space-y-2 px-4">
              <a href="#features" className="block py-2 text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#credentials" className="block py-2 text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Credentials</a>
              <a href="#journey" className="block py-2 text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <a href="#enterprise" className="block py-2 text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Enterprise</a>
              <Link to="/docs" className="block py-2 text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Docs</Link>
              <hr className="my-3" />
              <Link
                to="/signin"
                className="block w-full py-3 bg-primary-500 text-white font-medium rounded-lg text-center"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}
      </nav>
    );
  }

  // Minimal header with Back to Home link (for legal/info pages)
  if (variant === "minimal") {
    return (
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-success-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">ZKCN</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  // Standard header with unified Sign In (default)
  return (
    <nav className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-success-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">ZKCN</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/docs" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
              Docs
            </Link>
            <Link
              to="/signin"
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
